// KbCardPage — fetches a methodology-card content row from page_content
// and renders it via Lilach's existing EditorialPageFrame primitives.
// Visual is identical to a hand-rolled CARD_*.tsx — only the data flow
// changes: from inlined SECTIONS array to a Neon page_content row.

import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useApi } from "@/lib/api";
import {
  EditorialPageFrame,
  EditorialMarkdownSection,
  EditorialDivider,
  type TableOfContentsItem,
} from "@/components/editorial";
import type { KbCardContent } from "./types";
import { resolveIcon } from "./iconMap";

const slugifySection = (label: string, idx: number): string =>
  `section-${String(idx + 1).padStart(2, "0")}-${label
    .replace(/[^֐-׿a-zA-Z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "")
    .slice(0, 40)}`;

export function KbCardPage({ slug }: { slug: string }) {
  const api = useApi();

  const { data, isLoading, error } = useQuery({
    queryKey: ["kb", slug],
    queryFn: async (): Promise<KbCardContent> => {
      const res = await api(`/api/kb/${encodeURIComponent(slug)}`);
      if (!res.ok) {
        throw new Error(`Failed to load /api/kb/${slug}: HTTP ${res.status}`);
      }
      const json = (await res.json()) as { content: KbCardContent };
      return json.content;
    },
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div dir="rtl" className="flex items-center gap-2 p-8 text-sm text-stone-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        טוען…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div dir="rtl" className="p-8">
        <div className="rounded-2xl border border-rose-300/60 bg-rose-50/40 p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
          <div className="text-sm font-semibold text-rose-700">לא ניתן לטעון את הכרטיסייה</div>
          <div className="mt-1 text-xs text-stone-500">
            Slug: <code className="rounded bg-stone-100 px-1.5 py-0.5 font-mono">{slug}</code>
          </div>
          {error && (
            <div className="mt-2 text-xs text-rose-600/80">{(error as Error).message}</div>
          )}
        </div>
      </div>
    );
  }

  const Icon = resolveIcon(data.iconName);

  const tocItems: TableOfContentsItem[] = data.sections.map((s, idx) => ({
    title: s.label,
    id: slugifySection(s.label, idx),
  }));

  return (
    <div dir="rtl">
      <EditorialPageFrame
        eyebrow={data.eyebrow}
        title={data.title}
        subtitle={data.subtitle}
        icon={Icon}
        iconColor={data.iconColor ?? "rose"}
        toc={tocItems}
        tocHeading={data.tocHeading}
        footer={data.footer}
      >
        {data.sections.map((section, idx) => (
          <div key={idx}>
            {idx > 0 ? <EditorialDivider /> : null}
            <EditorialMarkdownSection
              index={idx + 1}
              title={section.label}
              body={section.body}
              id={tocItems[idx].id}
              credit={section.source ? `מקור: ${section.source}` : undefined}
            />
          </div>
        ))}
      </EditorialPageFrame>
    </div>
  );
}
